import React, { useCallback, useEffect, useState } from 'react';
import { runnerApi, downloadBlob, errorData, errorText, type DriftFix } from '../../../api/migrationRunner';
import { Alert, Btn, ErrorBox, Panel, Pill, Spinner, shortName } from './ui';
import type { RunnerNav } from './nav';

const KIND_LABEL: Record<string, string> = { table: 'table from template', column: 'from template', index: 'index from template' };

/**
 * Fix drift (approved mockup "Database redesign", Fix drift): a draft of
 * re-runnable SQL that brings one company back to the template. Run it on that
 * company (a recorded run, opened in the run sheet) or save it as a .sql file
 * to commit as a migration. Also lists the migrations that would fix it.
 */
const DriftFixSheet: React.FC<{ databaseName: string; isOwner: boolean; nav: RunnerNav; onRan?: () => void }> = ({ databaseName, isOwner, nav, onRan }) => {
  const [fix, setFix] = useState<DriftFix | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setFix(await runnerApi.drift(databaseName));
    } catch (err) {
      setError(errorText(err, 'Could not draft the fix'));
    } finally {
      setLoading(false);
    }
  }, [databaseName]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!fix) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await runnerApi.runDrift(databaseName, fix.sqlHash);
      onRan?.();
      if (r.rowId) nav.openRun(r.rowId);
      else setNotice('Started. It shows in History when it finishes.');
    } catch (err) {
      const d = errorData<{ fix?: DriftFix }>(err);
      if (d?.code === 'DRIFT_CHANGED' && d.fix) {
        setFix(d.fix);
        setNotice('The database changed since this draft was made. Here is the new draft; review it and run again.');
      } else {
        setError(errorText(err, 'The fix did not start'));
      }
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const sql = await runnerApi.driftFile(databaseName);
      const day = new Date();
      downloadBlob(sql, `drift-fix-${databaseName}-${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}.sql`, 'application/sql');
    } catch (err) {
      setError(errorText(err, 'Could not save the file'));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !fix) return <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> Comparing {databaseName} with the template…</div>;
  if (!fix) return error ? <ErrorBox>{error}</ErrorBox> : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-base text-slate-900">
          {fix.companyName} {fix.items.length ? `is missing ${fix.items.length} thing${fix.items.length === 1 ? '' : 's'}` : 'matches the template'}
        </b>
        <Pill tone={fix.items.length ? 'warn' : 'ok'}>{fix.items.length ? 'drift' : 'in sync'}</Pill>
      </div>

      {fix.items.length > 0 && (
        <Panel title="Draft fix, written for you" actions={<Pill tone="ok">safe to run twice</Pill>} bodyClass="divide-y divide-slate-100">
          {fix.items.map((it, n) => (
            <div key={`${it.kind}-${it.table}-${it.name}`} className="grid grid-cols-[24px_1fr_auto] items-start gap-3 px-4 py-2.5">
              <span className="pt-0.5 text-right font-mono text-xs text-slate-400">{n + 1}</span>
              <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words font-mono text-[12px] text-slate-800">{it.sql}</pre>
              <Pill tone="info">{KIND_LABEL[it.kind]}</Pill>
            </div>
          ))}
        </Panel>
      )}

      {fix.notes.length > 0 && (
        <ul className="space-y-1 text-[12.5px] text-slate-600">
          {fix.notes.map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}

      {fix.items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {isOwner && <Btn onClick={run} disabled={busy}>{busy ? 'Working…' : `Run on ${fix.companyName}`}</Btn>}
          <Btn kind="ghost" onClick={save} disabled={busy}>Save as a migration file</Btn>
          <Btn kind="ghost" onClick={load} disabled={busy || loading}>Re-check</Btn>
          <span className="text-xs text-slate-500">Runs with a backup first (per Settings) and the same lock as migrations; the company sees "Updating the system" meanwhile.</span>
        </div>
      )}

      <Panel title="Or: the migrations that would fix it">
        {fix.suggestions.length === 0 && <p className="text-[13px] text-slate-500">No unapplied migration here mentions these. The template has something no migration adds.</p>}
        <ul className="space-y-2 text-[13px]">
          {fix.suggestions.map((s) => (
            <li key={s.folder}>
              <b className="text-slate-900">{shortName(s.folder)}</b>{' '}
              <span className="text-slate-500">never run here · mentions {s.covers.join(', ')}</span>
              <div className="mt-0.5 flex flex-wrap gap-2">
                {s.files.map((f) => (
                  <button key={f.scriptId} type="button" onClick={() => nav.openPreview(f.scriptId)} className="text-[12.5px] font-semibold text-blue-600 hover:underline">
                    Preview {f.folderPath.split('/')[1]}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </Panel>

      {notice && <Alert tone="info" title={notice} />}
      {error && <ErrorBox>{error}</ErrorBox>}
    </div>
  );
};

export default DriftFixSheet;
